import React from 'react';
import { faLocationArrow } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Card,
	Col,
	Row,
} from 'antd';
import { Link } from 'react-router-dom';
import { VesselOwnershipTypeLabels } from '@shared/utils/constants';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import { formatDate } from '@client/utils/formatDate';
import VesselDescription from '@client/components/VesselDescription';
import Button from '@client/components/Button';
import Details from '@client/components/Details';
import VesselsLayer from '@client/components/Map/layers/VesselsLayer';
import Map, { Viewport } from '@client/components/Map/Map';
import VesselTimeline from '@client/components/VesselTimeline';
import styles from '@client/screens/fleet/VesselDetailsScreen/VesselDetailsScreen.module.css';
import { Links } from '@client/utils/links';

type VesselSummaryTabProps = {
	vessel: GetVesselDetailsResponse | undefined;
	position: Viewport | null;
};

export const VesselSummaryTab = ({
	vessel,
	position,
}: VesselSummaryTabProps) => {
	const getVesselSummaryItems = (v: GetVesselDetailsResponse) => [
		{
			key: 'name',
			label: 'Vessel name',
			value: (
				<VesselDescription
					name={v.name}
					flag={v.flag}
				/>
			),
		},
		{
			key: 'codename',
			label: 'Codename',
			value: v.codename,
		},
		{
			key: 'imo',
			label: 'IMO',
			value: v.imo,
		},
		{
			key: 'ownership',
			label: 'Ownership',
			value: VesselOwnershipTypeLabels[v.ownershipType],
		},
		{
			key: 'nextDryDocking',
			label: 'Next dry docking',
			value: formatDate(v.nextDryDocking),
		},
		{
			key: 'nextSpecialSurvey',
			label: 'Next special survey',
			value: formatDate(v.nextSpecialSurvey),
		},
	];

	if (vessel == null) {
		return null;
	}

	return (
		<Row gutter={[16, 16]}>
			{vessel.timelineData != null && vessel.timelineData.fixtures?.length > 0 && (
				<Col span={24}>
					<VesselTimeline
						data={vessel.timelineData}
					/>
				</Col>
			)}
			<Col span={16}>
				<Card
					title="Vessel on Map"
					extra={(
						<Link to={`${Links.Map.get(vessel.id)}`}>
							<Button
								icon={(<FontAwesomeIcon className={styles.icon} icon={faLocationArrow} />)}
								type="link"
							/>
							View on full map
						</Link>
					)}
					className={styles.mapCard}
				>
					<Map anchorPosition={position}>
						{() => (
							<>
								<VesselsLayer vesselIdsToShow={[vessel.id]} />
							</>
						)}
					</Map>
				</Card>
			</Col>
			<Col span={8}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Card title="Summary">
							<Details
								colon={false}
								bordered
								size="small"
								hideHeader
								boldValue
								items={getVesselSummaryItems(vessel)}
								clean
							/>
						</Card>
					</Col>
				</Row>
			</Col>
		</Row>
	);
};
